import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSubscription, updateSubscription } from '../services/api';
import ErrorModal from '../components/ErrorModal';
import CustomSelect from '../components/CustomSelect';

const EditSubscription = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadSubscription = useCallback(async () => {
    try {
      const response = await getSubscription(id);
      const subscription = response.data;
      
      setFormData({
        ...subscription,
        startDate: new Date(subscription.startDate).toISOString().split('T')[0],
        nextRenewalDate: new Date(subscription.nextRenewalDate).toISOString().split('T')[0],
        notifyEmails: subscription.notifyEmails || [],
        notes: subscription.notes || '',
        customDays: subscription.customDays || null,
        renewalUrl: subscription.renewalUrl || '',
        paymentCard: subscription.paymentCard || '',
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setError('Failed to load subscription');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? null : parseFloat(value)) : value,
    }));
  };

  const handleAddEmail = () => {
    if (emailInput && !formData.notifyEmails.includes(emailInput)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput)) {
        setError('Please enter a valid email address');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        notifyEmails: [...prev.notifyEmails, emailInput.trim()],
      }));
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email) => {
    setFormData((prev) => ({
      ...prev,
      notifyEmails: prev.notifyEmails.filter((e) => e !== email),
    }));
  };

  const calculateNextRenewal = () => {
    if (!formData) return '';
    const startDate = new Date(formData.startDate);
    let nextRenewal = new Date(startDate);
    
    switch (formData.duration) {
      case 'monthly':
        nextRenewal.setMonth(nextRenewal.getMonth() + 1);
        break;
      case 'quarterly':
        nextRenewal.setMonth(nextRenewal.getMonth() + 3);
        break;
      case 'yearly':
        nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
        break;
      case 'custom':
        if (formData.customDays) {
          nextRenewal.setDate(nextRenewal.getDate() + formData.customDays);
        }
        break;
    }
    
    return nextRenewal.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!formData.name || !formData.service || !formData.estimatedPrice) {
        setError('Please fill in all required fields');
        setSaving(false);
        return;
      }

      const submitData = {
        ...formData,
        estimatedPrice: parseFloat(formData.estimatedPrice),
        startDate: new Date(formData.startDate),
        nextRenewalDate: new Date(formData.nextRenewalDate || calculateNextRenewal()),
        customDays: formData.duration === 'custom' ? formData.customDays : null,
        renewalUrl: formData.renewalUrl?.trim() || null,
        paymentCard: formData.paymentCard?.trim() || null,
      };

      await updateSubscription(id, submitData);
      navigate('/subscriptions');
    } catch (error) {
      console.error('Error updating subscription:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      setError(errorMessage);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading subscription...</p>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Subscription not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/subscriptions"
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 inline-flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Subscriptions
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Edit Subscription</h1>
        <p className="text-gray-600 dark:text-gray-400">Update subscription details</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="space-y-6">
          {/* Same form fields as CreateSubscription */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Subscription Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Service/Provider <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Payment Duration <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'yearly', label: 'Yearly' },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
            </div>
            {formData.duration === 'custom' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Custom Days <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="customDays"
                  value={formData.customDays || ''}
                  onChange={handleChange}
                  required={formData.duration === 'custom'}
                  min="1"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Estimated Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="estimatedPrice"
                value={formData.estimatedPrice}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Currency
              </label>
              <input
                type="text"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                maxLength="3"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              {formData.startDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Calculated next renewal: {calculateNextRenewal()}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Next Renewal Date
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="nextRenewalDate"
                  value={formData.nextRenewalDate}
                  onChange={handleChange}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      nextRenewalDate: calculateNextRenewal(),
                    }))
                  }
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-400 dark:border-gray-500 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Use calculated
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                You can override this if you renewed early/late or need to correct a mistake.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Remind Days Before Renewal
            </label>
            <input
              type="number"
              name="reminderDaysBefore"
              value={formData.reminderDaysBefore}
              onChange={handleChange}
              min="0"
              max="30"
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Notification Emails
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter email address"
              />
              <button
                type="button"
                onClick={handleAddEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Add
              </button>
            </div>
            {formData.notifyEmails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.notifyEmails.map((email, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm border border-blue-200 dark:border-blue-700"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Renewal URL
                </label>
                <input
                  type="text"
                  name="renewalUrl"
                  value={formData.renewalUrl}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Portal or billing URL you use to renew"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Payment card / method
              </label>
              <input
                type="text"
                name="paymentCard"
                value={formData.paymentCard}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="e.g. Visa •••• 4242, Company card, Bank transfer"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Free text – you can store last 4 digits, card nickname, or payment method.
              </p>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Active Subscription
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Link
              to="/subscriptions"
              className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-center font-semibold"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all flex items-center justify-center"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>

      <ErrorModal
        isOpen={error !== null}
        onClose={() => setError(null)}
        title="Error"
        message={error || ''}
      />
    </div>
  );
};

export default EditSubscription;
